/**
 * SensitivitySection — Combined 5-parameter sensitivity analysis with AI insights.
 *
 * Calls POST /sensitivity/enhanced/combined/calculateCombinedAdjusted
 * Default range: ±6% (5 values → 5⁵ = 3,125 scenarios/year)
 *
 * After heatmap sections are built, auto-fires three n8n webhooks:
 *   POST https://dev-n8n.huhoka.com/webhook/combined/heatmap-analysis          (IC section)
 *   POST https://dev-n8n.huhoka.com/webhook/combined/heatmap-wacc-cap-analysis (WACC section)
 *   POST https://dev-n8n.huhoka.com/webhook/combined/heatmap-wacc-cap-analysis (CAP section)
 *
 * Layout:
 *   1. Header + controls (range dropdown + year dropdown)
 *   2. Base metrics strip
 *   3. Tornado chart + EV range card
 *   4. IC Section card  (heatmaps + AI insights)
 *   5. WACC Section card (heatmaps + AI insights)
 *   6. CAP Section card  (heatmaps + AI insights)
 *   7. Sensitivity index detail table
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import SpeedometerSection from './SpeedometerSection';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { Loader2, AlertCircle, Download } from 'lucide-react';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import SmartLoader from '../../ui/SmartLoader';
import LoadingCard from '../../ui/LoadingCard';
import FinancialNoData from '../FinancialNoData';
import { SENSITIVITY_PHASES, SENSITIVITY_EXPECTED_SEC } from '../loaderPhases';
import { useCombinedAdjustedSensitivity } from '../hooks/useSensitivityAnalysis';
import { exportCombinedAdjustedSensitivityXlsx } from '../../../lib/api/financialApi';
import { useSensitivityStore } from '../../../stores/sensitivityStore';
import { formatCurrencyValue, formatPercent } from '../utils/formatters';
import type {
  RegionConfig,
  CombinedAdjustedYearAnalysis,
  CombinedAdjustedSensitivityResponse,
  CombinedAdjustedScenario,
} from '../../../types/financial';

// ── Webhook URLs ──────────────────────────────────────────────────────────────

const WEBHOOK_BASE = 'https://dev-n8n.huhoka.com/webhook/combined';
const WEBHOOK_HEATMAP_ANALYSIS = `${WEBHOOK_BASE}/heatmap-analysis`;
const WEBHOOK_HEATMAP_WACC_CAP = `${WEBHOOK_BASE}/heatmap-wacc-cap-analysis`;
const WEBHOOK_TIMEOUT_MS = 540_000; // 9 min (matches source)

// ── Range presets ─────────────────────────────────────────────────────────────

const RANGE_OPTIONS = [
  { label: '±2%', values: [-0.02, -0.01, 0.0, 0.01, 0.02] as number[] },
  { label: '±4%', values: [-0.04, -0.02, 0.0, 0.02, 0.04] as number[] },
  { label: '±6%', values: [-0.06, -0.03, 0.0, 0.03, 0.06] as number[] }, // default
];
const DEFAULT_RANGE = '±6%';

// ── Insight response types ────────────────────────────────────────────────────

interface ScenarioInsight {
  scenario: string;
  insights: string[];
}

interface WebhookInsightOutput {
  scenario_insights?: ScenarioInsight[];
  executive_summary?: string;
  notes?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const pKey = (v: number) => `${Math.round(v * 100)}`;

function createGrid(
  filteredScenarios: CombinedAdjustedScenario[],
  revenueChanges: number[],
  ebitdaChanges: number[],
  baseEvEbitda: number
): Record<string, Record<string, number>> {
  const grid: Record<string, Record<string, number>> = {};
  ebitdaChanges.forEach((e) => {
    grid[pKey(e)] = {};
    revenueChanges.forEach((r) => {
      const matches = filteredScenarios.filter(
        (s) =>
          pKey(s.ebitda_change_percent) === pKey(e) &&
          pKey(s.revenue_change_percent) === pKey(r)
      );
      grid[pKey(e)][pKey(r)] =
        matches.length > 0
          ? matches.reduce((sum, s) => {
              if (!baseEvEbitda || baseEvEbitda <= 0) return sum;
              const pct = ((s.ev_ebitda_new - baseEvEbitda) / baseEvEbitda) * 100;
              return sum + pct;
            }, 0) / matches.length
          : 0;
    });
  });
  return grid;
}

function getAllSectionValues(
  tables: Array<{ grid: Record<string, Record<string, number>> }>
): number[] {
  return tables.flatMap((t) =>
    Object.values(t.grid).flatMap((row) => Object.values(row as Record<string, number>))
  );
}

function getSectionCellStyle(value: number, allValues: number[]): React.CSSProperties {
  const positives = allValues.filter((v) => v > 0);
  const negatives = allValues.filter((v) => v < 0);
  const maxPos = positives.length > 0 ? Math.max(...positives) : 1;
  const minNeg = negatives.length > 0 ? Math.min(...negatives) : -1;

  let bg = '#ffffff';
  if (value > 0) {
    const norm = Math.min(1, value / maxPos);
    if (norm <= 0.5) {
      const t = norm * 2;
      bg = `rgb(${Math.round(255 - (255 - 198) * t)},${Math.round(255 - (255 - 239) * t)},${Math.round(255 - (255 - 206) * t)})`;
    } else {
      const t = (norm - 0.5) * 2;
      bg = `rgb(${Math.round(198 - 198 * t)},${Math.round(239 - (239 - 176) * t)},${Math.round(206 - (206 - 80) * t)})`;
    }
  } else if (value < 0) {
    const norm = Math.min(1, Math.abs(value) / Math.abs(minNeg));
    if (norm <= 0.5) {
      const t = norm * 2;
      bg = `rgb(255,${Math.round(255 - (255 - 199) * t)},${Math.round(255 - (255 - 206) * t)})`;
    } else {
      const t = (norm - 0.5) * 2;
      bg = `rgb(${Math.round(255 - (255 - 220) * t)},${Math.round(199 - (199 - 53) * t)},${Math.round(206 - (206 - 69) * t)})`;
    }
  }
  const [r, g, b] = (bg.match(/\d+/g) ?? ['255', '255', '255']).map(Number);
  const toLinear = (c: number) => { const s = c / 255; return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return { backgroundColor: bg, color: luminance < 0.179 ? '#ffffff' : '#1a1a1a' };
}

function rgbToHex(rgb: string): string {
  if (rgb.startsWith('#')) return rgb;
  const m = rgb.match(/\d+/g);
  if (!m || m.length < 3) return '#FFFFFF';
  return (
    '#' +
    [m[0], m[1], m[2]]
      .map((x) => parseInt(x).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

// ── Webhook payload builder ───────────────────────────────────────────────────

interface GridTable {
  label: string;
  grid: Record<string, Record<string, number>>;
}

function buildWebhookPayload(
  company: string,
  tables: GridTable[],
  revenueChanges: number[],
  ebitdaChanges: number[],
  sectionAllValues: number[],
  scenarioLabels?: string[]
) {
  return {
    metadata: {
      company,
      metric: 'EV/EBITDA Sensitivity',
      value_represents: 'Percentage change in EV/EBITDA multiple',
      scenarios: scenarioLabels ?? tables.map((t) => t.label),
      x_axis_label: 'Change in Revenue (%)',
      y_axis_label: 'Change in EBITDA (%)',
    },
    data: tables.map((table) => {
      const matrix: Record<string, Array<{ value: number; color: string }>> = {};
      ebitdaChanges.forEach((e, rowIdx) => {
        matrix[`row_${rowIdx + 1}`] = revenueChanges.map((r) => {
          const val = table.grid[pKey(e)]?.[pKey(r)] ?? 0;
          const style = getSectionCellStyle(val, sectionAllValues);
          return {
            value: parseFloat(val.toFixed(2)),
            color: rgbToHex(style.backgroundColor as string),
          };
        });
      });
      return {
        scenario: table.label,
        x_values: revenueChanges.map((v) => parseFloat((v * 100).toFixed(0))),
        y_values: ebitdaChanges.map((v) => parseFloat((v * 100).toFixed(0))),
        matrix,
      };
    }),
  };
}

async function postWebhook(url: string, payload: object): Promise<WebhookInsightOutput | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), WEBHOOK_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) throw new Error(res.statusText);
    const json = await res.json();
    return (json.output ?? json) as WebhookInsightOutput;
  } catch {
    clearTimeout(timer);
    return null;
  }
}

// ── InsightsPanel ─────────────────────────────────────────────────────────────

const SCENARIO_ICONS = ['📊', '🔥', '💎', '📈', '⚡'];

function InsightsPanel({
  insights,
  loading,
}: {
  insights: WebhookInsightOutput | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="mt-4">
        <LoadingCard asCard={false} message="Generating AI insights…" height={120} delayMs={0} />
      </div>
    );
  }

  if (!insights) return null;

  return (
    <div className="mt-5 space-y-3">
      {/* Scenario insight cards */}
      {insights.scenario_insights && insights.scenario_insights.length > 0 && (
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${insights.scenario_insights.length}, minmax(0, 1fr))`,
          }}
        >
          {insights.scenario_insights.map((si, idx) => (
            <div
              key={idx}
              className="bg-neutral-50 border-2 border-violet-200 rounded-lg p-3 flex flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{SCENARIO_ICONS[idx % SCENARIO_ICONS.length]}</span>
                <span className="text-xs font-semibold text-neutral-800">{si.scenario}</span>
              </div>
              <ul className="list-disc pl-4 space-y-1">
                {si.insights?.map((point, pi) => (
                  <li key={pi} className="text-xs text-neutral-600 leading-relaxed">
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Executive summary */}
      {insights.executive_summary && (
        <div className="bg-neutral-50 border-2 border-violet-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📈</span>
            <span className="text-xs font-semibold text-neutral-800">Executive Summary</span>
          </div>
          <p className="text-xs text-neutral-600 leading-relaxed">{insights.executive_summary}</p>
          {insights.notes && (
            <div className="mt-2 border-l-4 border-amber-400 bg-amber-50 rounded-r-md p-2">
              <p className="text-xs font-semibold text-amber-700 mb-0.5">💡 Note:</p>
              <p className="text-xs text-amber-800 italic leading-relaxed">{insights.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── HeatmapGrid ───────────────────────────────────────────────────────────────

function HeatmapGrid({
  table,
  revenueChanges,
  ebitdaChanges,
  sectionAllValues,
}: {
  table: GridTable;
  revenueChanges: number[];
  ebitdaChanges: number[];
  sectionAllValues: number[];
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-center mb-1 text-violet-700">{table.label}</p>
      <p className="text-[10px] text-center text-neutral-400 mb-1">Change in Revenue →</p>
      {/* table-fixed + w-full: distributes columns evenly within the grid cell — no scroll */}
      <table className="text-[9px] border-collapse w-full table-fixed">
        <thead>
          <tr>
            <th className="p-0.5 w-7" />
            {revenueChanges.map((r) => (
              <th key={r} className="p-0.5 text-center font-semibold text-neutral-500">
                {(r * 100).toFixed(0)}%
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ebitdaChanges.map((e, rowIdx) => (
            <tr key={e}>
              <td className="p-0.5 text-center text-neutral-500 font-semibold relative">
                {rowIdx === Math.floor(ebitdaChanges.length / 2) && (
                  <span
                    className="absolute text-[8px] text-neutral-400 whitespace-nowrap pointer-events-none"
                    style={{ left: '-24px', top: '50%', transform: 'translateY(-50%) rotate(-90deg)' }}
                  >
                    EBITDA%
                  </span>
                )}
                {(e * 100).toFixed(0)}%
              </td>
              {revenueChanges.map((r) => {
                const value = table.grid[pKey(e)]?.[pKey(r)] ?? 0;
                return (
                  <td
                    key={r}
                    className="py-1 px-0.5 text-center font-semibold tabular-nums"
                    style={getSectionCellStyle(value, sectionAllValues)}
                    title={`Rev ${r >= 0 ? '+' : ''}${(r * 100).toFixed(0)}% / EBITDA ${e >= 0 ? '+' : ''}${(e * 100).toFixed(0)}% → EV ${value >= 0 ? '+' : ''}${value.toFixed(2)}%`}
                  >
                    {value.toFixed(2)}%
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tornado chart ─────────────────────────────────────────────────────────────

const TORNADO_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

function TornadoChart({ yearData }: { yearData: CombinedAdjustedYearAnalysis }) {
  const chartData = [...yearData.sensitivity_indices]
    .sort((a, b) => b.elasticity - a.elasticity)
    .map((idx) => ({
      name: idx.parameter.replace(/_/g, ' '),
      // elasticity is already in "EV % change per 1% param change" units — no *100
      value: parseFloat(idx.elasticity.toFixed(3)),
    }));

  return (
    <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 44)}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 64, left: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 11 }}
          tickFormatter={(v: number) => `${v.toFixed(1)}x`}
          domain={[0, 'auto']}
        />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
        <Tooltip
          formatter={(v: number) => [`${v.toFixed(3)}x`, 'Elasticity (EV% per 1% change)']}
          labelFormatter={(l) => `${l}`}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} label={{ position: 'right', formatter: (v: number) => `${v.toFixed(1)}x`, fontSize: 11, fill: '#6b7280' }}>
          {chartData.map((_e, i) => (
            <Cell key={i} fill={TORNADO_COLORS[i % TORNADO_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Base metrics strip ────────────────────────────────────────────────────────

function BaseMetricsStrip({ yearData, region }: { yearData: CombinedAdjustedYearAnalysis; region: RegionConfig }) {
  const m = yearData.base_metrics;
  const items = [
    { label: 'Base EV', value: formatCurrencyValue(m.total_ev, region) },
    { label: 'EBITDA Margin', value: formatPercent(m.ebitda_margin) },
    { label: 'ROIC', value: formatPercent(m.roic) },
    { label: 'WACC', value: formatPercent(m.wacc) },
    { label: 'CAP (adj)', value: `${m.cap.toFixed(1)} yrs` },
    { label: 'EV/EBITDA', value: `${m.ev_ebitda_ratio.toFixed(1)}x` },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      {items.map((item) => (
        <div key={item.label} className="bg-white border border-neutral-200 rounded-lg p-2.5">
          <p className="text-xs text-neutral-500 mb-0.5">{item.label}</p>
          <p className="text-sm font-bold text-neutral-900">{item.value}</p>
        </div>
      ))}
    </div>
  );
}

// ── Sensitivity indices table ─────────────────────────────────────────────────

function SensitivityTable({ yearData, region, maxChangePct }: { yearData: CombinedAdjustedYearAnalysis; region: RegionConfig; maxChangePct: number }) {
  const sorted = [...yearData.sensitivity_indices].sort((a, b) => a.rank - b.rank);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b-2 border-neutral-200 bg-neutral-50">
            {['Rank', 'Parameter', 'Elasticity', `EV at −${(maxChangePct * 100).toFixed(0)}%`, 'Base EV', `EV at +${(maxChangePct * 100).toFixed(0)}%`].map((h) => (
              <th key={h} className="py-2 px-3 text-left text-neutral-500 font-semibold uppercase tracking-wider first:pl-2">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((idx) => (
            <tr key={idx.parameter} className="border-b border-neutral-100 hover:bg-blue-50/30">
              <td className="py-2 pl-2 pr-3 font-bold text-neutral-700">#{idx.rank}</td>
              <td className="py-2 px-3 font-medium text-neutral-800">{idx.parameter.replace(/_/g, ' ')}</td>
              <td className="py-2 px-3 text-right tabular-nums font-semibold">{idx.elasticity.toFixed(3)}</td>
              <td className="py-2 px-3 text-right tabular-nums text-red-600">{formatCurrencyValue(idx.ev_at_negative_change, region)}</td>
              <td className="py-2 px-3 text-right tabular-nums text-neutral-700">{formatCurrencyValue(idx.ev_base, region)}</td>
              <td className="py-2 px-3 text-right tabular-nums text-emerald-600">{formatCurrencyValue(idx.ev_at_positive_change, region)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function yearLabel(fy: number | string): string {
  if (typeof fy === 'number') return `FY${String(fy).slice(-2)}`;
  return String(fy);
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  companyName: string;
  region: RegionConfig;
}

export default function SensitivitySection({ companyName, region }: Props) {
  // ── Zustand store (persists across tab switches) ──────────────────────────
  const store = useSensitivityStore();
  const storedRange = companyName ? store.getSelectedRange(companyName) : null;

  const [selectedRange, setSelectedRangeLocal] = useState(storedRange ?? DEFAULT_RANGE);
  const [selectedYear, setSelectedYearLocal] = useState<number | string | null>(null);
  const [localData, setLocalData] = useState<CombinedAdjustedSensitivityResponse | null>(null);

  // AI insights state
  const [icInsights, setIcInsights] = useState<WebhookInsightOutput | null>(null);
  const [icLoading, setIcLoading] = useState(false);
  const [waccInsights, setWaccInsights] = useState<WebhookInsightOutput | null>(null);
  const [waccLoading, setWaccLoading] = useState(false);
  const [capInsights, setCapInsights] = useState<WebhookInsightOutput | null>(null);
  const [capLoading, setCapLoading] = useState(false);

  // Webhook dedup refs (reset when data refreshes)
  const icSentRef = useRef(false);
  const waccSentRef = useRef(false);
  const capSentRef = useRef(false);

  const [isExporting, setIsExporting] = useState(false);

  const { mutate, isPending, isError, error } = useCombinedAdjustedSensitivity();

  // Wrap setters to also persist to store
  const setSelectedRange = (range: string) => {
    setSelectedRangeLocal(range);
    if (companyName) store.setSelectedRange(companyName, range);
  };

  const setSelectedYear = (year: number | string | null) => {
    setSelectedYearLocal(year);
    if (companyName) store.updateSelectedYear(companyName, selectedRange, year);
  };

  // Auto-run on mount and range change — restore from store or fetch
  useEffect(() => {
    if (!companyName) return;

    // Try zustand store first (survives tab switches)
    const cached = store.getCached(companyName, selectedRange);
    if (cached) {
      setLocalData(cached.data);
      setSelectedYearLocal(cached.selectedYear);
      setIcInsights(cached.icInsights as WebhookInsightOutput | null);
      setWaccInsights(cached.waccInsights as WebhookInsightOutput | null);
      setCapInsights(cached.capInsights as WebhookInsightOutput | null);
      // Mark webhooks as already sent so they don't re-fire
      icSentRef.current = cached.icInsights != null;
      waccSentRef.current = cached.waccInsights != null;
      capSentRef.current = cached.capInsights != null;
      return;
    }

    // Reset webhook flags when re-fetching
    icSentRef.current = false;
    waccSentRef.current = false;
    capSentRef.current = false;
    setIcInsights(null);
    setWaccInsights(null);
    setCapInsights(null);

    const values = RANGE_OPTIONS.find((o) => o.label === selectedRange)?.values ?? RANGE_OPTIONS[2].values;
    mutate(
      { company: companyName, scenario_change_percent: values },
      {
        onSuccess: (res: CombinedAdjustedSensitivityResponse) => {
          setLocalData(res);
          const realYears = res.data.filter((d) => typeof d.financial_year === 'number');
          const defaultFY =
            realYears.length > 0
              ? realYears.reduce((max, d) => Number(d.financial_year) > Number(max.financial_year) ? d : max).financial_year
              : res.data[0]?.financial_year ?? null;
          setSelectedYearLocal(defaultFY);
          // Persist to zustand store
          store.setCache(companyName, selectedRange, {
            data: res,
            selectedYear: defaultFY,
            selectedRange,
            icInsights: null,
            waccInsights: null,
            capInsights: null,
          });
          store.setSelectedRange(companyName, selectedRange);
        },
      }
    );
  }, [companyName, selectedRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const yearData =
    localData?.data?.find((d) => d.financial_year === selectedYear) ?? localData?.data?.[0];

  // ── Build heatmap sections from yearData ──────────────────────────────────
  const heatmapSections = useMemo(() => {
    if (!yearData?.scenarios?.length) return null;

    const { scenarios } = yearData;
    const baseEvEbitda = yearData.base_metrics.ev_ebitda_ratio;
    const icChanges = [...new Set(scenarios.map((s) => s.invested_capital_change_percent))].sort((a, b) => a - b);
    const waccChanges = [...new Set(scenarios.map((s) => s.wacc_change_percent))].sort((a, b) => a - b);
    const capChanges = [...new Set(scenarios.map((s) => s.cap_change_percent))].sort((a, b) => a - b);
    const revenueChanges = [...new Set(scenarios.map((s) => s.revenue_change_percent))].sort((a, b) => a - b);
    const ebitdaChanges = [...new Set(scenarios.map((s) => s.ebitda_change_percent))].sort((a, b) => a - b);

    const selectThreeExtreme = (arr: number[]) =>
      arr.length >= 3 ? [arr[0], arr[Math.floor(arr.length / 2)], arr[arr.length - 1]] : arr;
    const selectThreeMiddle = (arr: number[]) => {
      if (arr.length < 3) return arr;
      const mid = Math.floor(arr.length / 2);
      return arr.length >= 5 ? [arr[mid - 1], arr[mid], arr[mid + 1]] : [arr[0], arr[mid], arr[arr.length - 1]];
    };

    // IC Section
    const icTables: GridTable[] = selectThreeExtreme(icChanges).map((ic) => {
      const f = scenarios.filter((s) => pKey(s.invested_capital_change_percent) === pKey(ic) && pKey(s.wacc_change_percent) === '0' && pKey(s.cap_change_percent) === '0');
      const p = ic * 100;
      return { label: p < 0 ? `Lower IC (${p.toFixed(0)}%)` : p === 0 ? 'Base IC' : `Higher IC (+${p.toFixed(0)}%)`, grid: createGrid(f, revenueChanges, ebitdaChanges, baseEvEbitda) };
    });

    // WACC Section
    const waccRows = selectThreeMiddle(waccChanges)
      .filter((_, i, arr) => i === 0 || i === arr.length - 1)
      .map((wacc) => {
        const wp = wacc * 100;
        const waccLabel = wp < 0 ? `WACC ${wp.toFixed(0)}%` : `WACC +${wp.toFixed(0)}%`;
        const tables: GridTable[] = selectThreeExtreme(icChanges).map((ic) => {
          const f = scenarios.filter((s) => pKey(s.wacc_change_percent) === pKey(wacc) && pKey(s.invested_capital_change_percent) === pKey(ic) && pKey(s.cap_change_percent) === '0');
          const p = ic * 100;
          return { label: p < 0 ? `IC ${p.toFixed(0)}%` : p === 0 ? 'IC Base' : `IC +${p.toFixed(0)}%`, grid: createGrid(f, revenueChanges, ebitdaChanges, baseEvEbitda) };
        });
        return { waccLabel, tables };
      });

    // CAP Section
    const capRows = [capChanges[0], capChanges[capChanges.length - 1]].map((cap) => {
      const cp = cap * 100;
      const capLabel = cp < 0 ? `CAP ${cp.toFixed(0)}%` : `CAP +${cp.toFixed(0)}%`;
      const tables: GridTable[] = selectThreeExtreme(icChanges).map((ic) => {
        const f = scenarios.filter((s) => pKey(s.cap_change_percent) === pKey(cap) && pKey(s.invested_capital_change_percent) === pKey(ic) && pKey(s.wacc_change_percent) === '0');
        const p = ic * 100;
        return { label: p < 0 ? `IC ${p.toFixed(0)}%` : p === 0 ? 'IC Base' : `IC +${p.toFixed(0)}%`, grid: createGrid(f, revenueChanges, ebitdaChanges, baseEvEbitda) };
      });
      return { capLabel, tables };
    });

    return {
      icSection: { title: 'Revenue vs EBITDA for each Invested Capital', subtitle: 'WACC and CAP at 0% change', tables: icTables },
      waccSection: { title: 'Revenue vs EBITDA for each WACC', subtitle: 'Showing IC variations for extreme WACC scenarios, CAP at 0% change', rows: waccRows },
      capSection: { title: 'Revenue vs EBITDA for each CAP', subtitle: 'Showing IC variations for extreme CAP scenarios, WACC at 0% change', rows: capRows },
      revenueChanges,
      ebitdaChanges,
    };
  }, [yearData]);

  // ── Auto-fire IC webhook ──────────────────────────────────────────────────
  useEffect(() => {
    if (!heatmapSections || !localData || icSentRef.current) return;
    icSentRef.current = true;
    setIcLoading(true);

    const { icSection, revenueChanges, ebitdaChanges } = heatmapSections;
    const allVals = getAllSectionValues(icSection.tables);
    const payload = buildWebhookPayload(localData.company, icSection.tables, revenueChanges, ebitdaChanges, allVals, icSection.tables.map((t) => t.label));

    postWebhook(WEBHOOK_HEATMAP_ANALYSIS, payload).then((result) => {
      setIcInsights(result);
      setIcLoading(false);
      if (companyName) store.updateInsights(companyName, selectedRange, { icInsights: result });
    });
  }, [heatmapSections, localData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-fire WACC webhook ────────────────────────────────────────────────
  useEffect(() => {
    if (!heatmapSections || !localData || waccSentRef.current) return;
    waccSentRef.current = true;
    setWaccLoading(true);

    const { waccSection, revenueChanges, ebitdaChanges } = heatmapSections;
    const allTables = waccSection.rows.flatMap((r) => r.tables);
    const allVals = getAllSectionValues(allTables);
    const payload = buildWebhookPayload(localData.company, allTables, revenueChanges, ebitdaChanges, allVals);

    postWebhook(WEBHOOK_HEATMAP_WACC_CAP, payload).then((result) => {
      setWaccInsights(result);
      setWaccLoading(false);
      if (companyName) store.updateInsights(companyName, selectedRange, { waccInsights: result });
    });
  }, [heatmapSections, localData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-fire CAP webhook ─────────────────────────────────────────────────
  useEffect(() => {
    if (!heatmapSections || !localData || capSentRef.current) return;
    capSentRef.current = true;
    setCapLoading(true);

    const { capSection, revenueChanges, ebitdaChanges } = heatmapSections;
    const allTables = capSection.rows.flatMap((r) => r.tables);
    const allVals = getAllSectionValues(allTables);
    const payload = buildWebhookPayload(localData.company, allTables, revenueChanges, ebitdaChanges, allVals);

    postWebhook(WEBHOOK_HEATMAP_WACC_CAP, payload).then((result) => {
      setCapInsights(result);
      setCapLoading(false);
      if (companyName) store.updateInsights(companyName, selectedRange, { capInsights: result });
    });
  }, [heatmapSections, localData]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentRangeValues = RANGE_OPTIONS.find((o) => o.label === selectedRange)?.values ?? [];
  const maxChangePct = currentRangeValues.length > 0 ? Math.max(...currentRangeValues.map(Math.abs)) : 0.06;

  return (
    <div className="space-y-4">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3 pb-2 border-b border-neutral-200">
        <div>
          <h2 className="text-base font-bold text-neutral-900">5-Parameter Sensitivity Analysis</h2>
          <p className="text-xs text-neutral-500 mt-0.5">
            Combined analysis varying Revenue, EBITDA, Invested Capital, WACC, and CAP
          </p>
        </div>
        {localData && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
              {localData.total_scenarios.toLocaleString()} Scenarios
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
              5 Parameters
            </span>
          </div>
        )}
      </div>

      {/* ── Controls ────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-neutral-500">Sensitivity Range</label>
          <select
            value={selectedRange}
            onChange={(e) => setSelectedRange(e.target.value)}
            disabled={isPending}
            className="px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-white text-neutral-800 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-50"
          >
            {RANGE_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.label}>{opt.label}</option>
            ))}
          </select>
        </div>

        {localData && localData.data.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">Year</label>
            <select
              value={String(selectedYear ?? '')}
              onChange={(e) => {
                const raw = e.target.value;
                const num = Number(raw);
                setSelectedYear(isNaN(num) ? raw : num);
              }}
              className="px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-white text-neutral-800 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              {[...localData.data]
                .sort((a, b) => Number(b.financial_year) - Number(a.financial_year))
                .map((d) => (
                  <option key={String(d.financial_year)} value={String(d.financial_year)}>
                    {yearLabel(d.financial_year)}
                  </option>
                ))}
            </select>
          </div>
        )}

        {localData && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">&nbsp;</label>
            <button
              onClick={async () => {
                setIsExporting(true);
                try {
                  const values = RANGE_OPTIONS.find((o) => o.label === selectedRange)?.values ?? [];
                  await exportCombinedAdjustedSensitivityXlsx(companyName, values);
                } catch {
                  // toast not available here — silent fail with console
                  console.error('Failed to export sensitivity analysis');
                } finally {
                  setIsExporting(false);
                }
              }}
              disabled={isExporting || isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export XLSX
            </button>
          </div>
        )}

      </div>

      {/* ── Loading ──────────────────────────────────────────────────────────── */}
      {isPending && !localData && (
        <SmartLoader
          title="Running sensitivity analysis"
          subtitle="Combined 5-parameter scenario grid"
          phases={SENSITIVITY_PHASES}
          progress={{ mode: 'elapsed', expectedSec: SENSITIVITY_EXPECTED_SEC }}
          active={isPending}
          accent="violet"
        />
      )}

      {/* ── Error ───────────────────────────────────────────────────────────── */}
      {isError && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Analysis failed</p>
            <p className="text-xs text-red-700 mt-0.5">
              {(error as any)?.response?.data?.detail ?? (error as any)?.message ?? 'Unknown error'}
            </p>
          </div>
        </div>
      )}

      {/* ── Results ─────────────────────────────────────────────────────────── */}
      {localData && yearData && (
        <>
          <BaseMetricsStrip yearData={yearData} region={region} />

          {/* ── IC Section ──────────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <h3 className="text-sm font-semibold text-violet-700">
                {heatmapSections?.icSection.title ?? 'Revenue vs EBITDA for each Invested Capital'}
              </h3>
              <p className="text-xs text-neutral-500 mt-0.5">
                {heatmapSections?.icSection.subtitle ?? 'WACC and CAP at 0% change'}
              </p>
            </CardHeader>
            <CardBody>
              {isPending ? (
                <LoadingCard asCard={false} message="Building heatmaps…" height={160} delayMs={0} />
              ) : !heatmapSections ? (
                <FinancialNoData asCard={false} state="metric-missing" metricLabel="Sensitivity scenarios" minHeight={160} />
              ) : (
                <>
                  <div
                    className="grid gap-4"
                    style={{ gridTemplateColumns: `repeat(${heatmapSections.icSection.tables.length}, minmax(0, 1fr))` }}
                  >
                    {heatmapSections.icSection.tables.map((table, i) => (
                      <HeatmapGrid
                        key={i}
                        table={table}
                        revenueChanges={heatmapSections.revenueChanges}
                        ebitdaChanges={heatmapSections.ebitdaChanges}
                        sectionAllValues={getAllSectionValues(heatmapSections.icSection.tables)}
                      />
                    ))}
                  </div>
                  <InsightsPanel insights={icInsights} loading={icLoading} />
                </>
              )}
            </CardBody>
          </Card>

          {/* ── WACC Section ─────────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <h3 className="text-sm font-semibold text-violet-700">
                {heatmapSections?.waccSection.title ?? 'Revenue vs EBITDA for each WACC'}
              </h3>
              <p className="text-xs text-neutral-500 mt-0.5">
                {heatmapSections?.waccSection.subtitle ?? 'Showing IC variations for extreme WACC scenarios, CAP at 0% change'}
              </p>
            </CardHeader>
            <CardBody>
              {isPending ? (
                <LoadingCard asCard={false} message="Building heatmaps…" height={160} delayMs={0} />
              ) : !heatmapSections ? (
                <FinancialNoData asCard={false} state="metric-missing" metricLabel="Sensitivity scenarios" minHeight={160} />
              ) : (
                <>
                  <div className="space-y-6">
                    {heatmapSections.waccSection.rows.map((row, rowIdx) => (
                      <div key={rowIdx}>
                        <p className="text-xs font-semibold text-violet-600 mb-3">{row.waccLabel}</p>
                        <div className="grid grid-cols-3 gap-4">
                          {row.tables.map((table, tIdx) => (
                            <HeatmapGrid
                              key={tIdx}
                              table={table}
                              revenueChanges={heatmapSections.revenueChanges}
                              ebitdaChanges={heatmapSections.ebitdaChanges}
                              sectionAllValues={getAllSectionValues(heatmapSections.waccSection.rows.flatMap((r) => r.tables))}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <InsightsPanel insights={waccInsights} loading={waccLoading} />
                </>
              )}
            </CardBody>
          </Card>

          {/* ── CAP Section ──────────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <h3 className="text-sm font-semibold text-violet-700">
                {heatmapSections?.capSection.title ?? 'Revenue vs EBITDA for each CAP'}
              </h3>
              <p className="text-xs text-neutral-500 mt-0.5">
                {heatmapSections?.capSection.subtitle ?? 'Showing IC variations for extreme CAP scenarios, WACC at 0% change'}
              </p>
            </CardHeader>
            <CardBody>
              {isPending ? (
                <LoadingCard asCard={false} message="Building heatmaps…" height={160} delayMs={0} />
              ) : !heatmapSections ? (
                <FinancialNoData asCard={false} state="metric-missing" metricLabel="Sensitivity scenarios" minHeight={160} />
              ) : (
                <>
                  <div className="space-y-6">
                    {heatmapSections.capSection.rows.map((row, rowIdx) => (
                      <div key={rowIdx}>
                        <p className="text-xs font-semibold text-violet-600 mb-3">{row.capLabel}</p>
                        <div className="grid grid-cols-3 gap-4">
                          {row.tables.map((table, tIdx) => (
                            <HeatmapGrid
                              key={tIdx}
                              table={table}
                              revenueChanges={heatmapSections.revenueChanges}
                              ebitdaChanges={heatmapSections.ebitdaChanges}
                              sectionAllValues={getAllSectionValues(heatmapSections.capSection.rows.flatMap((r) => r.tables))}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <InsightsPanel insights={capInsights} loading={capLoading} />
                </>
              )}
            </CardBody>
          </Card>

          {/* ── Speedometer (EV/EBITDA live sensitivity) ─────────────────────── */}
          <SpeedometerSection companyName={companyName} region={region} />
        </>
      )}
    </div>
  );
}
